import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Store } from "@ngrx/store";
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject, timer } from "rxjs";
import { retry, takeUntil } from "rxjs/operators";
import { MatTooltipModule } from '@angular/material/tooltip';

import { AuthService } from "../../services/auth.service";
import { HeaderService } from "../../services/header.service";
import { PopupService } from "../../services/popup.service";
import { AppState } from "../../state/app.state";

import * as WorkspaceActions from '../../state/workspace/workspace.actions';
import * as WorkspaceSelectors from '../../state/workspace/workspace.selectors';
import { Workspace, CreateWorkspacePayload } from '../../models/workspace.model';

// ICP imports
import * as IcpActions from '../../state/icp/icp.actions';
import * as IcpSelectors from '../../state/icp/icp.selectors';
import { ICP } from '../../state/icp/icp.state';
import { IcpService } from '../../services/icp.service';

interface NewWorkspace {
  name: string;
  description: string;
  instructions: string;
  icpId?: number;
}

interface FileUploadStatus {
  fileName: string;
  status: 'uploading' | 'success' | 'error';
}

@Component({
  selector: 'app-workspaces',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule, RouterModule],
  templateUrl: './workspaces.component.html',
  styleUrl: './workspaces.component.scss'
})
export class WorkspacesComponent implements OnInit, OnDestroy {
  @ViewChild('workspaceForm') workspaceForm!: NgForm;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  private destroy$ = new Subject<void>();
  private icpsCache: ICP[] = [];

  workspaces$: Observable<Workspace[]>;
  loading$: Observable<boolean>;
  error$: Observable<string | null>;

  // ICP observables
  icps$: Observable<ICP[]>;
  icpsLoading$: Observable<boolean>;
  icpsError$: Observable<string | null>;

  user: any = { has_completed_setup: undefined };
  creatingWorkspace = false;
  uploadingFiles = false;
  selectedFiles: File[] = [];
  uploadStatus: FileUploadStatus[] = [];
  lastCreatedWorkspaceId: string | null = null;

  newWorkspace: NewWorkspace = {
    name: '',
    description: '',
    instructions: '',
    icpId: undefined
  };

  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private headerService: HeaderService,
    private router: Router,
    private actions$: Actions,
    private popupService: PopupService,
    private icpService: IcpService
  ) {
    // Initialize observables
    this.workspaces$ = this.store.select(WorkspaceSelectors.selectAllWorkspaces);
    this.loading$ = this.store.select(WorkspaceSelectors.selectWorkspacesLoading);
    this.error$ = this.store.select(WorkspaceSelectors.selectWorkspacesError);

    // Initialize ICP observables
    this.icps$ = this.store.select(IcpSelectors.selectIcps);
    this.icpsLoading$ = this.store.select(IcpSelectors.selectIcpsLoading);
    this.icpsError$ = this.store.select(IcpSelectors.selectIcpsError);
  }

  ngOnInit(): void {
    this.headerService.setHeaderState('Workspaces', null, true);

    // Load workspaces from API
    this.store.dispatch(WorkspaceActions.loadWorkspaces());

    // Load ICPs for the dropdown
    this.store.dispatch(IcpActions.loadIcps({ page: 1, perPage: 100 }));

    // Cache ICPs and auto-select the first one (re-applies after form reset)
    this.icps$.pipe(takeUntil(this.destroy$)).subscribe(icps => {
      this.icpsCache = icps ?? [];
      if (this.icpsCache.length > 0 && !this.newWorkspace.icpId) {
        this.newWorkspace.icpId = this.icpsCache[0].id;
      }
    });

    // Get user data
    this.authService.authUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (user) {
            this.user = user;
            localStorage.setItem('user_setup_completed', user.has_completed_setup === 1 ? 'true' : 'false');
          }
        },
      });

    // Subscribe to workspace creation success
    this.actions$.pipe(
      ofType(WorkspaceActions.createWorkspaceSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.creatingWorkspace = false;
      this.lastCreatedWorkspaceId = action.workspace.id;

      // Store files to upload and ICP ID before clearing the form
      const filesToUpload = [...this.selectedFiles];
      const selectedIcpId = this.newWorkspace.icpId;

      // Clear form immediately after successful creation
      this.resetForm();

      // Link ICP to workspace if selected — retry once on transient failures
      if (selectedIcpId) {
        const workspaceId = action.workspace.id;
        this.icpService.linkIcpToWorkspace(workspaceId, selectedIcpId).pipe(
          retry({ count: 1, delay: () => timer(1000) }),
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {},
          error: () => {
            this.popupService.notify(
              `Workspace created, but linking the ICP failed. Open "${action.workspace.name}" and retry from "Edit workspace".`,
              'ICP link failed',
              'warning'
            );
          }
        });
      }

      // If there are files to upload, start uploading them
      if (filesToUpload.length > 0) {
        this.uploadingFiles = true;
        this.uploadStatus = [{
          fileName: filesToUpload[0].name,
          status: 'uploading' as const
        }];

        this.store.dispatch(WorkspaceActions.uploadFilesToWorkspace({
          workspaceId: action.workspace.id,
          files: filesToUpload
        }));
      } else {
        // No files to upload, just show success notification
        this.popupService.notify('Workspace has been successfully created!', 'Workspace created', 'success');
      }
    });

    // Subscribe to workspace creation failure
    this.actions$.pipe(
      ofType(WorkspaceActions.createWorkspaceFailure),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.creatingWorkspace = false;
      this.popupService.notify('Failed to create workspace. Please try again.', 'Error', 'error');
    });

    // Subscribe to file upload success
    this.actions$.pipe(
      ofType(WorkspaceActions.uploadFileSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      const index = this.uploadStatus.findIndex(s => s.fileName === action.fileName);
      if (index !== -1) {
        this.uploadStatus[index].status = 'success';
      }
    });

    // Subscribe to file upload failure
    this.actions$.pipe(
      ofType(WorkspaceActions.uploadFileFailure),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      const index = this.uploadStatus.findIndex(s => s.fileName === action.fileName);
      if (index !== -1) {
        this.uploadStatus[index].status = 'error';
      }
    });

    // Subscribe to upload complete
    this.actions$.pipe(
      ofType(WorkspaceActions.uploadFilesComplete),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      // Hide the upload status immediately
      this.uploadingFiles = false;
      this.uploadStatus = [];

      if (action.successCount > 0) {
        this.popupService.notify('Workspace has been successfully created!', 'Workspace created', 'success');
        // Refresh list so the new row's total_files / formatted_file_size reflect the uploaded files
        this.store.dispatch(WorkspaceActions.loadWorkspaces());
      } else if (this.lastCreatedWorkspaceId) {
        // Workspace was created but every file upload failed — don't leave the user in the dark
        this.popupService.notify(
          'Workspace created, but file upload failed. You can re-upload it from "Edit workspace".',
          'Upload failed',
          'warning'
        );
      }

      this.lastCreatedWorkspaceId = null;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectWorkspace(workspace: Workspace): void {
    this.store.dispatch(WorkspaceActions.selectWorkspace({ workspace }));
    this.router.navigate(['/workspaces', workspace.id]);
  }

  createNewWorkspace(event: Event): void {
    event.preventDefault();

    if (!this.newWorkspace.name.trim()) {
      this.popupService.notify('Workspace name is required.', 'Missing name', 'warning');
      return;
    }

    if (!this.newWorkspace.icpId) {
      this.popupService.notify('Please select an ICP for this workspace.', 'Missing ICP', 'warning');
      return;
    }

    this.creatingWorkspace = true;

    const workspaceData: CreateWorkspacePayload = {
      name: this.newWorkspace.name.trim(),
      description: this.newWorkspace.description.trim() || undefined,
      instructions: this.newWorkspace.instructions.trim() || undefined,
      is_default: false // Always set to false
    };

    // Dispatch create action
    this.store.dispatch(WorkspaceActions.createWorkspace({ workspace: workspaceData }));
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0]; // Only take the first file

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        this.popupService.notify(`File ${file.name} exceeds 10MB limit`, 'File too large', 'error');
        input.value = '';
        return;
      }

      // Replace the selected files array with just this one file
      this.selectedFiles = [file];

      // Reset input to allow selecting the same file again
      input.value = '';
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private resetForm(): void {
    // Reset the model — re-auto-select first ICP since the selector won't re-emit
    const defaultIcpId = this.icpsCache.length > 0 ? this.icpsCache[0].id : undefined;
    this.newWorkspace = {
      name: '',
      description: '',
      instructions: '',
      icpId: defaultIcpId
    };

    // Clear selected files
    this.selectedFiles = [];

    // Reset Angular form state (dirty/touched/validation) without clobbering the model above
    if (this.workspaceForm) {
      this.workspaceForm.resetForm(this.newWorkspace);
    }

    // Clear the file input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
